/**
 * Create Request Page
 * Allows yacht owners to create mooring assistance requests OR crew position openings
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { requestsAPI, yachtsAPI, portsAPI } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import {
  MapPin,
  Calendar,
  Clock,
  Ship,
  Users,
  ArrowLeft,
  ArrowRight,
  Search,
  CheckCircle,
  Briefcase,
  Award,
  Globe,
  Anchor,
  UserPlus,
  Info,
} from 'lucide-react';
import { format, addDays } from 'date-fns';

// Constants for crew requirements
const DEPARTMENTS = [
  { value: 'deck', label: 'Deck' },
  { value: 'interior', label: 'Interior' },
  { value: 'galley', label: 'Galley' },
  { value: 'engineering', label: 'Engineering' },
];

const POSITIONS = {
  deck: [
    { value: 'deck_greenie', label: 'Greenie (Green Deckhand)' },
    { value: 'deck_deckhand', label: 'Deckhand' },
    { value: 'deck_bosun', label: 'Bosun' },
    { value: 'deck_first_mate', label: 'First Mate / Chief Officer' },
    { value: 'deck_captain', label: 'Captain' },
  ],
  interior: [
    { value: 'interior_junior_stew', label: 'Junior Stewardess' },
    { value: 'interior_stew', label: 'Stewardess' },
    { value: 'interior_2nd_stew', label: '2nd Stewardess' },
    { value: 'interior_chief_stew', label: 'Chief Stewardess' },
    { value: 'interior_purser', label: 'Purser' },
  ],
  galley: [
    { value: 'galley_junior_chef', label: 'Junior Chef' },
    { value: 'galley_sole_chef', label: 'Sole Chef' },
    { value: 'galley_head_chef', label: 'Head Chef' },
  ],
  engineering: [
    { value: 'eng_junior_engineer', label: 'Junior Engineer' },
    { value: 'eng_2nd_engineer', label: '2nd Engineer' },
    { value: 'eng_chief_engineer', label: 'Chief Engineer' },
  ],
};

const CERTIFICATION_GROUPS = [
  {
    label: 'Safety & Medical',
    certs: [
      { id: 'cert_stcw_bst', label: 'STCW Basic Safety Training (BST)' },
      { id: 'cert_eng1', label: 'ENG1 Medical Certificate' },
      { id: 'cert_adv_fire', label: 'Advanced Fire Fighting' },
      { id: 'cert_pscrb', label: 'Proficiency in Survival Craft (PSCRB)' },
      { id: 'cert_medical_care', label: 'Medical Care (STCW A-VI/4)' },
      { id: 'cert_sso', label: 'Ship Security Officer (SSO)' },
      { id: 'cert_ism_isps', label: 'ISM & ISPS Familiarisation' },
    ],
  },
  {
    label: 'Deck & Navigation',
    certs: [
      { id: 'cert_powerboat_l2', label: 'Powerboat Level 2' },
      { id: 'cert_ym_coastal', label: 'Yachtmaster Coastal' },
      { id: 'cert_ym_offshore', label: 'Yachtmaster Offshore' },
      { id: 'cert_ym_ocean', label: 'Yachtmaster Ocean' },
      { id: 'cert_oow_3000', label: 'Officer of the Watch (≤3000 GT)' },
      { id: 'cert_master_3000', label: 'Master (≤3000 GT)' },
      { id: 'cert_master_200', label: 'Master 200 GT' },
      { id: 'cert_gmdss_goc', label: 'GMDSS General Operator Certificate' },
      { id: 'cert_brm', label: 'Bridge Resource Management' },
      { id: 'cert_ecdis', label: 'ECDIS (Electronic Chart Display)' },
      { id: 'cert_dp', label: 'Dynamic Positioning' },
    ],
  },
  {
    label: 'Interior & Hospitality',
    certs: [
      { id: 'cert_food_safety_l2', label: 'Food Safety Level 2' },
      { id: 'cert_food_safety_l3', label: 'Food Safety Level 3' },
      { id: 'cert_wset_l1', label: 'WSET Level 1 (Wines)' },
      { id: 'cert_wset_l2', label: 'WSET Level 2 (Wines)' },
      { id: 'cert_wset_l3', label: 'WSET Level 3 (Wines)' },
    ],
  },
  {
    label: 'Galley / Chef',
    certs: [
      { id: 'cert_ships_cook', label: "Ship's Cook Certificate" },
    ],
  },
  {
    label: 'Engineering',
    certs: [
      { id: 'cert_aec', label: 'Approved Engine Course (AEC)' },
      { id: 'cert_y4', label: 'Yacht Engineer Y4' },
      { id: 'cert_y3', label: 'Yacht Engineer Y3' },
      { id: 'cert_y2', label: 'Yacht Engineer Y2' },
      { id: 'cert_y1', label: 'Yacht Engineer Y1' },
      { id: 'cert_eto', label: 'Electro-Technical Officer (ETO)' },
    ],
  },
  {
    label: 'Water Sports & Other',
    certs: [
      { id: 'cert_padi_ow', label: 'PADI Open Water Diver' },
      { id: 'cert_padi_dm', label: 'PADI Divemaster' },
      { id: 'cert_rya_pwc', label: 'RYA Jet Ski License (PWC)' },
    ],
  },
];

const LANGUAGES = [
  { value: 'lang_english', label: 'English' },
  { value: 'lang_french', label: 'French' },
  { value: 'lang_spanish', label: 'Spanish' },
  { value: 'lang_italian', label: 'Italian' },
  { value: 'lang_german', label: 'German' },
  { value: 'lang_dutch', label: 'Dutch' },
  { value: 'lang_russian', label: 'Russian' },
];

const DEPARTMENT_CERT_GROUPS = {
  deck: ['Safety & Medical', 'Deck & Navigation', 'Water Sports & Other'],
  interior: ['Safety & Medical', 'Interior & Hospitality'],
  galley: ['Safety & Medical', 'Galley / Chef', 'Interior & Hospitality'],
  engineering: ['Safety & Medical', 'Engineering'],
};

const POSITION_DEFAULT_CERTS = {
  deck_greenie: ['cert_stcw_bst', 'cert_eng1'],
  deck_deckhand: ['cert_stcw_bst', 'cert_eng1', 'cert_powerboat_l2'],
  deck_bosun: ['cert_stcw_bst', 'cert_eng1', 'cert_powerboat_l2'],
  deck_first_mate: ['cert_stcw_bst', 'cert_eng1', 'cert_oow_3000', 'cert_gmdss_goc', 'cert_adv_fire', 'cert_medical_care', 'cert_pscrb'],
  deck_captain: ['cert_stcw_bst', 'cert_eng1', 'cert_master_3000', 'cert_gmdss_goc', 'cert_adv_fire', 'cert_medical_care', 'cert_pscrb', 'cert_sso', 'cert_ism_isps'],
  interior_junior_stew: ['cert_stcw_bst', 'cert_eng1'],
  interior_stew: ['cert_stcw_bst', 'cert_eng1', 'cert_food_safety_l2'],
  interior_2nd_stew: ['cert_stcw_bst', 'cert_eng1', 'cert_food_safety_l2'],
  interior_chief_stew: ['cert_stcw_bst', 'cert_eng1', 'cert_food_safety_l2'],
  interior_purser: ['cert_stcw_bst', 'cert_eng1', 'cert_food_safety_l2'],
  galley_junior_chef: ['cert_stcw_bst', 'cert_eng1', 'cert_ships_cook', 'cert_food_safety_l2'],
  galley_sole_chef: ['cert_stcw_bst', 'cert_eng1', 'cert_ships_cook', 'cert_food_safety_l2'],
  galley_head_chef: ['cert_stcw_bst', 'cert_eng1', 'cert_ships_cook', 'cert_food_safety_l3'],
  eng_junior_engineer: ['cert_stcw_bst', 'cert_eng1'],
  eng_2nd_engineer: ['cert_stcw_bst', 'cert_eng1', 'cert_aec'],
  eng_chief_engineer: ['cert_stcw_bst', 'cert_eng1', 'cert_adv_fire', 'cert_pscrb'],
};

function CreateRequest() {
  const navigate = useNavigate();
  const { showToast } = useNotifications();

  // Request mode: null = not chosen, 'mooring' = dock help, 'position' = crew job
  const [requestMode, setRequestMode] = useState(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [yacht, setYacht] = useState(null);
  const [portSearch, setPortSearch] = useState('');
  const [portResults, setPortResults] = useState([]);
  const [searchingPorts, setSearchingPorts] = useState(false);

  const [formData, setFormData] = useState({
    yacht_id: '',
    port_id: '',
    port_name: '',
    // Mooring helper fields
    scheduled_date: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
    eta_from: '10:00',
    eta_to: '11:00',
    request_type: 'scheduled',
    helpers_needed: 1,
    mooring_type: 'med',
    special_notes: '',
    // Position open fields
    start_date: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
    end_date: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
    crew_department: '',
    crew_position: '',
    min_experience_years: 0,
    required_certifications: [],
    preferred_certifications: [],
    contract_type: 'temporary',
    required_languages: [],
    salary: '',
    salary_currency: 'EUR',
  });

  useEffect(() => {
    loadYacht();
  }, []);

  useEffect(() => {
    const searchPorts = async () => {
      if (portSearch.length < 2) {
        setPortResults([]);
        return;
      }

      setSearchingPorts(true);
      try {
        const data = await portsAPI.search(portSearch);
        setPortResults(data.ports || data.data || []);
      } catch (error) {
        console.error('Port search failed:', error);
      } finally {
        setSearchingPorts(false);
      }
    };

    const debounce = setTimeout(searchPorts, 300);
    return () => clearTimeout(debounce);
  }, [portSearch]);

  const loadYacht = async () => {
    try {
      const data = await yachtsAPI.getMyYachts();
      const yachtsList = data.yachts || data.data || [];
      if (yachtsList.length > 0) {
        setYacht(yachtsList[0]);
        setFormData(prev => ({ ...prev, yacht_id: yachtsList[0].id }));
      }
    } catch (error) {
      console.error('Failed to load yacht:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePortSelect = (port) => {
    setFormData(prev => ({
      ...prev,
      port_id: port.id,
      port_name: port.name,
    }));
    setPortSearch(port.name);
    setPortResults([]);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Add request_category to distinguish between mooring and position
      const submitData = {
        ...formData,
        request_category: requestMode,
      };
      const response = await requestsAPI.create(submitData);
      showToast('Request created successfully!', 'success');
      const requestId = response.data?.id || response.request?.id || response.id;
      navigate(`/owner/requests/${requestId}`);
    } catch (error) {
      showToast(error.message || 'Failed to create request', 'error');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    if (requestMode === 'mooring') {
      switch (step) {
        case 1:
          return formData.port_id && formData.scheduled_date && formData.eta_from && formData.eta_to;
        case 2:
          return formData.helpers_needed > 0;
        default:
          return true;
      }
    } else if (requestMode === 'position') {
      switch (step) {
        case 1:
          return formData.port_id && formData.crew_department && formData.crew_position;
        case 2:
          return formData.start_date && formData.end_date;
        case 3:
          return true;
        default:
          return true;
      }
    }
    return false;
  };

  const getTotalSteps = () => {
    return requestMode === 'mooring' ? 2 : 3;
  };

  const handlePositionChange = (position) => {
    setFormData(prev => {
      const defaultCerts = POSITION_DEFAULT_CERTS[position] || [];
      return {
        ...prev,
        crew_position: position,
        required_certifications: defaultCerts,
        preferred_certifications: [],
      };
    });
  };

  const handleCertificationToggle = (certId, type) => {
    setFormData(prev => {
      const newState = { ...prev };

      if (type === 'required') {
        if (newState.required_certifications.includes(certId)) {
          newState.required_certifications = newState.required_certifications.filter(id => id !== certId);
        } else {
          newState.required_certifications = [...newState.required_certifications, certId];
          newState.preferred_certifications = newState.preferred_certifications.filter(id => id !== certId);
        }
      } else if (type === 'preferred') {
        if (newState.preferred_certifications.includes(certId)) {
          newState.preferred_certifications = newState.preferred_certifications.filter(id => id !== certId);
        } else {
          newState.preferred_certifications = [...newState.preferred_certifications, certId];
          newState.required_certifications = newState.required_certifications.filter(id => id !== certId);
        }
      }

      return newState;
    });
  };

  const toggleLanguage = (langValue) => {
    setFormData(prev => ({
      ...prev,
      required_languages: prev.required_languages.includes(langValue)
        ? prev.required_languages.filter(l => l !== langValue)
        : [...prev.required_languages, langValue],
    }));
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  // MODE SELECTION SCREEN
  if (!requestMode) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-navy-600 hover:text-navy-800 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </button>
          <h1 className="text-2xl font-display font-bold text-navy-900">
            What do you need?
          </h1>
          <p className="text-navy-600 mt-1">
            Choose the type of help you're looking for
          </p>
        </div>

        {yacht && (
          <div className="card mb-6 bg-gray-50">
            <div className="flex items-center">
              <div className="h-12 w-12 bg-white rounded-lg flex items-center justify-center border border-gray-200">
                <Ship className="h-6 w-6 text-navy-400" />
              </div>
              <div className="ml-4">
                <p className="font-medium text-navy-900">{yacht.name}</p>
                <p className="text-sm text-navy-500">
                  {yacht.length_m ? `${yacht.length_m}m` : ''} {yacht.yacht_type === 'motor' ? 'Motor Yacht' : yacht.yacht_type === 'sailing' ? 'Sailing Yacht' : yacht.yacht_type}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Mooring Helper Option */}
          <button
            onClick={() => setRequestMode('mooring')}
            className="card p-6 text-left hover:shadow-lg transition-shadow border-2 border-transparent hover:border-primary-500"
          >
            <div className="h-14 w-14 bg-primary-100 rounded-xl flex items-center justify-center mb-4">
              <Anchor className="h-7 w-7 text-primary-600" />
            </div>
            <h3 className="text-lg font-semibold text-navy-900 mb-2">
              Mooring Helper
            </h3>
            <p className="text-sm text-navy-600">
              Need help docking? Request assistance for mooring, anchoring, or line handling at a specific date and time.
            </p>
            <div className="mt-4 flex items-center text-primary-600 text-sm font-medium">
              Quick dock assistance
              <ArrowRight className="h-4 w-4 ml-1" />
            </div>
          </button>

          {/* Position Open Option */}
          <button
            onClick={() => setRequestMode('position')}
            className="card p-6 text-left hover:shadow-lg transition-shadow border-2 border-transparent hover:border-gold-500"
          >
            <div className="h-14 w-14 bg-gold-100 rounded-xl flex items-center justify-center mb-4">
              <UserPlus className="h-7 w-7 text-gold-600" />
            </div>
            <h3 className="text-lg font-semibold text-navy-900 mb-2">
              Position Open
            </h3>
            <p className="text-sm text-navy-600">
              Looking for crew? Post a job opening for a specific position with date range and requirements.
            </p>
            <div className="mt-4 flex items-center text-gold-600 text-sm font-medium">
              Hire crew member
              <ArrowRight className="h-4 w-4 ml-1" />
            </div>
          </button>
        </div>
      </div>
    );
  }

  // MOORING HELPER FLOW
  if (requestMode === 'mooring') {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <button
            onClick={() => setRequestMode(null)}
            className="flex items-center text-navy-600 hover:text-navy-800 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to options
          </button>
          <h1 className="text-2xl font-display font-bold text-navy-900">
            Request Mooring Help
          </h1>
          <p className="text-navy-600 mt-1">
            Tell us when and where you need dock assistance
          </p>
        </div>

        {yacht && (
          <div className="card mb-6 bg-gray-50">
            <div className="flex items-center">
              <div className="h-12 w-12 bg-white rounded-lg flex items-center justify-center border border-gray-200">
                <Ship className="h-6 w-6 text-navy-400" />
              </div>
              <div className="ml-4">
                <p className="font-medium text-navy-900">{yacht.name}</p>
                <p className="text-sm text-navy-500">
                  {yacht.length_m ? `${yacht.length_m}m` : ''} {yacht.yacht_type === 'motor' ? 'Motor Yacht' : yacht.yacht_type === 'sailing' ? 'Sailing Yacht' : yacht.yacht_type}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Progress Steps */}
        <div className="flex items-center mb-8">
          {[1, 2].map((s) => (
            <React.Fragment key={s}>
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-full font-medium ${
                  step >= s ? 'bg-primary-600 text-white' : 'bg-gray-200 text-navy-500'
                }`}
              >
                {step > s ? <CheckCircle className="h-5 w-5" /> : s}
              </div>
              {s < 2 && (
                <div className={`flex-1 h-1 mx-2 ${step > s ? 'bg-primary-600' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Location & Time */}
        {step === 1 && (
          <div className="card">
            <h2 className="text-lg font-display font-semibold text-navy-900 mb-6">
              When & Where?
            </h2>

            {/* Port Search */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-navy-700 mb-2">
                <MapPin className="h-4 w-4 inline mr-1" />
                Port / Marina
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={portSearch}
                  onChange={(e) => {
                    setPortSearch(e.target.value);
                    if (formData.port_id) {
                      setFormData(prev => ({ ...prev, port_id: '', port_name: '' }));
                    }
                  }}
                  className="input-field pl-10"
                  placeholder="Search for a port or marina..."
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />

                {portResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {portResults.map((port) => (
                      <button
                        key={port.id}
                        onClick={() => handlePortSelect(port)}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                      >
                        <p className="font-medium text-navy-900">{port.name}</p>
                        <p className="text-sm text-navy-500">{port.city}, {port.country}</p>
                      </button>
                    ))}
                  </div>
                )}

                {searchingPorts && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="spinner w-5 h-5" />
                  </div>
                )}
              </div>

              {formData.port_id && (
                <p className="mt-2 text-sm text-green-600 flex items-center">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  {formData.port_name} selected
                </p>
              )}
            </div>

            {/* Date */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-navy-700 mb-2">
                <Calendar className="h-4 w-4 inline mr-1" />
                Date
              </label>
              <input
                type="date"
                name="scheduled_date"
                value={formData.scheduled_date}
                onChange={handleChange}
                min={format(new Date(), 'yyyy-MM-dd')}
                className="input-field"
              />
            </div>

            {/* Time */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  <Clock className="h-4 w-4 inline mr-1" />
                  ETA From
                </label>
                <input
                  type="time"
                  name="eta_from"
                  value={formData.eta_from}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  <Clock className="h-4 w-4 inline mr-1" />
                  ETA To
                </label>
                <input
                  type="time"
                  name="eta_to"
                  value={formData.eta_to}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
            </div>

            {/* Request Type */}
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-2">
                Request Type
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, request_type: 'scheduled' }))}
                  className={`p-4 rounded-lg border-2 text-left transition-colors ${
                    formData.request_type === 'scheduled'
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Calendar className="h-5 w-5 text-primary-600 mb-2" />
                  <p className="font-medium text-navy-900">Scheduled</p>
                  <p className="text-sm text-navy-500">Plan ahead</p>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, request_type: 'real_time' }))}
                  className={`p-4 rounded-lg border-2 text-left transition-colors ${
                    formData.request_type === 'real_time'
                      ? 'border-gold-500 bg-gold-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Clock className="h-5 w-5 text-gold-600 mb-2" />
                  <p className="font-medium text-navy-900">Real-time</p>
                  <p className="text-sm text-navy-500">Need help now</p>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Details */}
        {step === 2 && (
          <div className="card">
            <h2 className="text-lg font-display font-semibold text-navy-900 mb-6">
              Additional Details
            </h2>

            <div className="mb-6">
              <label className="block text-sm font-medium text-navy-700 mb-2">
                <Users className="h-4 w-4 inline mr-1" />
                Helpers Needed
              </label>
              <div className="flex items-center space-x-3">
                {[1, 2, 3, 4].map((num) => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, helpers_needed: num }))}
                    className={`w-12 h-12 rounded-lg font-medium transition-colors ${
                      formData.helpers_needed === num
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-navy-700 hover:bg-gray-200'
                    }`}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-navy-700 mb-2">
                Mooring Type
              </label>
              <select
                name="mooring_type"
                value={formData.mooring_type}
                onChange={handleChange}
                className="input-field"
              >
                <option value="med">Mediterranean (stern-to)</option>
                <option value="alongside">Alongside</option>
                <option value="bow">Bow-to</option>
                <option value="anchor">Anchor assistance</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-navy-700 mb-2">
                Additional Notes (optional)
              </label>
              <textarea
                name="special_notes"
                value={formData.special_notes}
                onChange={handleChange}
                rows={4}
                className="input-field"
                placeholder="Any special instructions or requirements..."
              />
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          {step > 1 ? (
            <button onClick={() => setStep(step - 1)} className="btn-secondary">
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </button>
          ) : (
            <div />
          )}

          {step < 2 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ArrowRight className="h-5 w-5 ml-2" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!canProceed() || loading}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center">
                  <span className="spinner h-5 w-5 mr-2" />
                  Creating...
                </span>
              ) : (
                <>
                  Create Request
                  <CheckCircle className="h-5 w-5 ml-2" />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    );
  }

  // POSITION OPEN FLOW
  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-8">
        <button
          onClick={() => setRequestMode(null)}
          className="flex items-center text-navy-600 hover:text-navy-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to options
        </button>
        <h1 className="text-2xl font-display font-bold text-navy-900">
          Post a Position
        </h1>
        <p className="text-navy-600 mt-1">
          Find the right crew member for your yacht
        </p>
      </div>

      {yacht && (
        <div className="card mb-6 bg-gray-50">
          <div className="flex items-center">
            <div className="h-12 w-12 bg-white rounded-lg flex items-center justify-center border border-gray-200">
              <Ship className="h-6 w-6 text-navy-400" />
            </div>
            <div className="ml-4">
              <p className="font-medium text-navy-900">{yacht.name}</p>
              <p className="text-sm text-navy-500">
                {yacht.length_m ? `${yacht.length_m}m` : ''} {yacht.yacht_type === 'motor' ? 'Motor Yacht' : yacht.yacht_type === 'sailing' ? 'Sailing Yacht' : yacht.yacht_type}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Progress Steps */}
      <div className="flex items-center mb-8">
        {[1, 2, 3].map((s) => (
          <React.Fragment key={s}>
            <div
              className={`flex items-center justify-center w-10 h-10 rounded-full font-medium ${
                step >= s ? 'bg-gold-500 text-white' : 'bg-gray-200 text-navy-500'
              }`}
            >
              {step > s ? <CheckCircle className="h-5 w-5" /> : s}
            </div>
            {s < 3 && (
              <div className={`flex-1 h-1 mx-2 ${step > s ? 'bg-gold-500' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Position & Location */}
      {step === 1 && (
        <div className="card">
          <h2 className="text-lg font-display font-semibold text-navy-900 mb-6">
            Position Details
          </h2>

          {/* Department */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-navy-700 mb-2">
              <Briefcase className="h-4 w-4 inline mr-1" />
              Department
            </label>
            <select
              value={formData.crew_department}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                crew_department: e.target.value,
                crew_position: '',
                required_certifications: [],
                preferred_certifications: [],
              }))}
              className="input-field"
            >
              <option value="">Select a department</option>
              {DEPARTMENTS.map(dept => (
                <option key={dept.value} value={dept.value}>
                  {dept.label}
                </option>
              ))}
            </select>
          </div>

          {/* Position */}
          {formData.crew_department && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-navy-700 mb-2">
                <Users className="h-4 w-4 inline mr-1" />
                Position to Fill
              </label>
              <select
                value={formData.crew_position}
                onChange={(e) => handlePositionChange(e.target.value)}
                className="input-field"
              >
                <option value="">Select a position</option>
                {POSITIONS[formData.crew_department]?.map(pos => (
                  <option key={pos.value} value={pos.value}>
                    {pos.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Port Search */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-navy-700 mb-2">
              <MapPin className="h-4 w-4 inline mr-1" />
              Location / Port
            </label>
            <div className="relative">
              <input
                type="text"
                value={portSearch}
                onChange={(e) => {
                  setPortSearch(e.target.value);
                  if (formData.port_id) {
                    setFormData(prev => ({ ...prev, port_id: '', port_name: '' }));
                  }
                }}
                className="input-field pl-10"
                placeholder="Search for a port or marina..."
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />

              {portResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                  {portResults.map((port) => (
                    <button
                      key={port.id}
                      onClick={() => handlePortSelect(port)}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                    >
                      <p className="font-medium text-navy-900">{port.name}</p>
                      <p className="text-sm text-navy-500">{port.city}, {port.country}</p>
                    </button>
                  ))}
                </div>
              )}

              {searchingPorts && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="spinner w-5 h-5" />
                </div>
              )}
            </div>

            {formData.port_id && (
              <p className="mt-2 text-sm text-green-600 flex items-center">
                <CheckCircle className="h-4 w-4 mr-1" />
                {formData.port_name} selected
              </p>
            )}
          </div>
        </div>
      )}

      {/* Step 2: Dates & Contract */}
      {step === 2 && (
        <div className="card">
          <h2 className="text-lg font-display font-semibold text-navy-900 mb-6">
            Contract Period
          </h2>

          {/* Selected position summary */}
          {formData.crew_position && (
            <div className="mb-6 p-4 bg-gold-50 rounded-lg border border-gold-200">
              <p className="text-sm text-gold-800">
                <strong>Position:</strong> {POSITIONS[formData.crew_department]?.find(p => p.value === formData.crew_position)?.label}
              </p>
            </div>
          )}

          {/* Start Date & End Date */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-2">
                <Calendar className="h-4 w-4 inline mr-1" />
                Start Date
              </label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                min={format(new Date(), 'yyyy-MM-dd')}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-2">
                <Calendar className="h-4 w-4 inline mr-1" />
                End Date
              </label>
              <input
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                min={formData.start_date}
                className="input-field"
              />
            </div>
          </div>

          {/* Contract Type */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-navy-700 mb-2">
              Contract Type
            </label>
            <select
              value={formData.contract_type}
              onChange={(e) => setFormData(prev => ({ ...prev, contract_type: e.target.value }))}
              className="input-field"
            >
              <option value="permanent">Permanent</option>
              <option value="seasonal">Seasonal</option>
              <option value="temporary">Temporary / Day Work</option>
              <option value="delivery">Delivery</option>
            </select>
            {/* DAYWORK: Info note for temporary/daywork positions */}
            {formData.contract_type === 'temporary' && (
              <p className="mt-2 text-sm text-teal-700 bg-teal-50 p-3 rounded-lg flex items-start">
                <Info className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Day Work positions</strong> are visible to both <strong>Mooring Helpers</strong> and <strong>Experienced Crew</strong>.
                  You'll see a badge on each applicant showing their experience level.
                </span>
              </p>
            )}
          </div>

          {/* Min Experience */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-navy-700 mb-2">
              Minimum Experience (years)
            </label>
            <input
              type="number"
              min="0"
              max="30"
              value={formData.min_experience_years}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                min_experience_years: parseInt(e.target.value) || 0,
              }))}
              className="input-field"
            />
          </div>

          {/* Salary */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-navy-700 mb-2">
              Monthly Salary (optional)
            </label>
            <div className="flex space-x-3">
              <input
                type="number"
                min="0"
                placeholder="e.g. 3500"
                value={formData.salary}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  salary: e.target.value,
                }))}
                className="input-field flex-1"
              />
              <select
                value={formData.salary_currency}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  salary_currency: e.target.value,
                }))}
                className="input-field w-24"
              >
                <option value="EUR">€ EUR</option>
                <option value="USD">$ USD</option>
              </select>
            </div>
          </div>

          {/* Notes */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-navy-700 mb-2">
              Job Description (optional)
            </label>
            <textarea
              name="special_notes"
              value={formData.special_notes}
              onChange={handleChange}
              rows={4}
              className="input-field"
              placeholder="Describe the role, responsibilities, and any special requirements..."
            />
          </div>

          {/* Required Certificates Selection */}
          {formData.crew_position && (
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-1">
                <Award className="h-4 w-4 inline mr-1" />
                Required Certifications
              </label>
              <p className="text-xs text-navy-500 mb-3">
                Pre-selected based on position. Tick the ones you require.
              </p>
              <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
                {CERTIFICATION_GROUPS
                  .filter(group => (DEPARTMENT_CERT_GROUPS[formData.crew_department] || []).includes(group.label))
                  .map((group) => (
                    <div key={group.label} className="mb-3">
                      <p className="text-xs font-semibold text-navy-600 uppercase tracking-wider mb-2">
                        {group.label}
                      </p>
                      {group.certs.map((cert) => {
                        const isRequired = formData.required_certifications.includes(cert.id);
                        return (
                          <label key={cert.id} className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={isRequired}
                              onChange={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  required_certifications: isRequired
                                    ? prev.required_certifications.filter(id => id !== cert.id)
                                    : [...prev.required_certifications, cert.id]
                                }));
                              }}
                              className="w-4 h-4 text-gold-600 rounded border-gray-300"
                            />
                            <span className="ml-3 text-sm text-navy-700">{cert.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Languages */}
      {step === 3 && (
        <div className="card">
          <h2 className="text-lg font-display font-semibold text-navy-900 mb-6">
            Language Requirements (Optional)
          </h2>

          {/* Summary */}
          {formData.crew_position && (
            <div className="mb-6 p-4 bg-gold-50 rounded-lg border border-gold-200">
              <p className="text-sm text-gold-800 mb-2">
                <strong>Position:</strong> {POSITIONS[formData.crew_department]?.find(p => p.value === formData.crew_position)?.label}
              </p>
              <p className="text-sm text-gold-800">
                <strong>Required Certificates:</strong> {formData.required_certifications.length} selected
              </p>
            </div>
          )}

          {/* Required Languages */}
          <div>
            <label className="block text-sm font-medium text-navy-700 mb-3">
              <Globe className="h-4 w-4 inline mr-1" />
              Required Languages (optional)
            </label>
            <div className="space-y-2">
              {LANGUAGES.map((lang) => (
                <label key={lang.value} className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.required_languages.includes(lang.value)}
                    onChange={() => toggleLanguage(lang.value)}
                    className="w-4 h-4 text-gold-600 rounded border-gray-300"
                  />
                  <span className="ml-3 text-sm text-navy-700">{lang.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between mt-8">
        {step > 1 ? (
          <button onClick={() => setStep(step - 1)} className="btn-secondary">
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back
          </button>
        ) : (
          <div />
        )}

        {step < 3 ? (
          <button
            onClick={() => setStep(step + 1)}
            disabled={!canProceed()}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ArrowRight className="h-5 w-5 ml-2" />
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={!canProceed() || loading}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center">
                <span className="spinner h-5 w-5 mr-2" />
                Creating...
              </span>
            ) : (
              <>
                Post Position
                <CheckCircle className="h-5 w-5 ml-2" />
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

export default CreateRequest;
